/**
 * useMessageHandler - SSE 消息处理 Hook
 * 提取自 ChatPage.tsx 的 handleIncomingMessage 函数
 *
 * 主要功能：
 * 1. 消息路由 - 确定消息应该路由到哪个对话
 * 2. treeData 更新 - 更新树形数据结构
 * 3. 状态更新 - 更新消息状态（waiting, ongoing 等）
 * 4. 内容更新 - 更新消息内容（流式更新）
 * 5. ID 更新 - 更新 backendId 和 previousMessageId
 */

import { useCallback, useRef } from 'react';
import { Message, Conversation, MessageBlock } from '@/types';
import { useUIStore } from '@/stores';
import {
  resolveTargetConversationId,
  findConversationByFrontendTempId,
  isStatusOnlyUpdate,
  getUserInfo,
  isPendingId,
} from '@/utils/messageUtils';

interface UseMessageHandlerOptions {
  conversationsRef: React.MutableRefObject<Conversation[]>;
  setConversations: React.Dispatch<React.SetStateAction<Conversation[]>>;
  setSidebarProjectConversationsCache: (
    updater: (
      prev: Record<string, Conversation[]>
    ) => Record<string, Conversation[]>
  ) => void;
  currentConversationIdRef: React.MutableRefObject<string | undefined>;
  setCurrentConversationId: (id: string | undefined) => void;
  updateConversationMessage: (conversationId: string, message: Message) => void;
  updateConversationLoadingState: (
    conversationId: string,
    isLoading: boolean
  ) => void;
  loadConversationDetails: (conversationId: string, force?: boolean) => void;
}

export function useMessageHandler({
  conversationsRef,
  setConversations,
  setSidebarProjectConversationsCache,
  currentConversationIdRef,
  setCurrentConversationId,
  updateConversationMessage,
  updateConversationLoadingState,
  loadConversationDetails,
}: UseMessageHandlerOptions) {
  // Refs for stable function references
  const setConversationsRef = useRef(setConversations);
  const setSidebarProjectConversationsCacheRef = useRef(
    setSidebarProjectConversationsCache
  );
  const setCurrentConversationIdRef = useRef(setCurrentConversationId);
  const updateConversationMessageRef = useRef(updateConversationMessage);
  const updateConversationLoadingStateRef = useRef(
    updateConversationLoadingState
  );
  const loadConversationDetailsRef = useRef(loadConversationDetails);

  // Update refs
  setConversationsRef.current = setConversations;
  setSidebarProjectConversationsCacheRef.current =
    setSidebarProjectConversationsCache;
  setCurrentConversationIdRef.current = setCurrentConversationId;
  updateConversationMessageRef.current = updateConversationMessage;
  updateConversationLoadingStateRef.current = updateConversationLoadingState;
  loadConversationDetailsRef.current = loadConversationDetails;

  /**
   * 执行 temp- ID 到真实 ID 的转换
   */
  const convertTempToRealId = useCallback(
    (oldTempId: string, newConvId: string): Conversation | null => {
      console.log('⏰ Converting temp ID to real ID:', {
        oldTempId,
        newConvId,
      });

      // 同步更新 conversationAgentMap 中的 ID
      const { conversationAgentMap, setConversationAgent } =
        useUIStore.getState();
      const agent = conversationAgentMap[oldTempId];
      if (agent) {
        setConversationAgent(newConvId, agent);
      }

      let convertedConversation: Conversation | null = null;

      // 更新 conversations store
      setConversationsRef.current(prev => {
        const tempConv = prev.find(c => c.id === oldTempId);
        if (!tempConv) {
          console.error('❌ Temp conversation not found:', oldTempId);
          return prev;
        }

        const converted = {
          ...tempConv,
          id: newConvId,
          conversationId: newConvId,
        };
        convertedConversation = converted;

        return prev
          .filter(conv => conv.id !== oldTempId && conv.id !== newConvId)
          .concat(converted);
      });

      // 更新 sidebar cache
      setSidebarProjectConversationsCacheRef.current(prev => {
        const newCache = { ...prev };
        Object.keys(newCache).forEach(pid => {
          const tempConv = (newCache[pid] || []).find(c => c.id === oldTempId);
          if (tempConv) {
            const converted = {
              ...tempConv,
              id: newConvId,
              conversationId: newConvId,
            };
            newCache[pid] = (newCache[pid] || [])
              .filter(conv => conv.id !== oldTempId && conv.id !== newConvId)
              .concat(converted);
          }
        });
        return newCache;
      });

      // 更新 currentConversationId
      if (currentConversationIdRef.current === oldTempId) {
        setCurrentConversationIdRef.current(newConvId);
        currentConversationIdRef.current = newConvId;
      }

      // 🔥 同步更新 activeSkillConversations（temp ID → 真实 ID）
      // 否则 WebSocket 事件中的真实 conversationId 在 store 中找不到匹配
      const uiState = useUIStore.getState();
      if (uiState.isSkillConversation(oldTempId)) {
        console.log('🎯 convertTempToRealId: migrating skill conversation', {
          oldTempId,
          newConvId,
        });
        uiState.removeSkillConversation(oldTempId);
        uiState.addSkillConversation(newConvId);
      }

      return convertedConversation;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  /**
   * 更新 treeData 节点状态
   */
  const updateTreeNodeStatus = useCallback(
    (
      targetConversationId: string,
      pendingId: string,
      role: 'user' | 'assistant',
      newStatus: string | undefined
    ) => {
      setConversationsRef.current(prev =>
        prev.map(conv => {
          if (conv.id !== targetConversationId || !conv.treeData) return conv;

          const updateNodeStatus = (nodes: any[]): any[] => {
            return nodes.map(node => {
              // 🔥 修改：同时检查 node.id 和 node.backendId
              // 因为 node.id 保持为 pendingId，而流式更新可能传入后端 ID
              // 🔥 新增：对于 user 节点，也检查 `user-${pendingId}` 格式
              const nodeIdStr = String(node.id);
              const nodeBackendIdStr = node.backendId
                ? String(node.backendId)
                : '';
              const isIdMatch =
                nodeIdStr === pendingId ||
                nodeBackendIdStr === pendingId ||
                nodeIdStr === `user-${pendingId}`; // 用户节点 ID 格式
              if (isIdMatch && node.role === role) {
                return { ...node, messageStatus: newStatus };
              }
              if (node.children && node.children.length > 0) {
                return { ...node, children: updateNodeStatus(node.children) };
              }
              return node;
            });
          };

          return { ...conv, treeData: updateNodeStatus(conv.treeData) };
        })
      );
    },
    []
  );

  /**
   * 更新 treeData 节点内容
   */
  const updateTreeNodeContents = useCallback(
    (targetConversationId: string, pendingId: string, message: Message) => {
      setConversationsRef.current(prev =>
        prev.map(conv => {
          if (conv.id !== targetConversationId || !conv.treeData) return conv;

          const updateNodeContents = (nodes: any[]): any[] => {
            return nodes.map(node => {
              // 🔥 修改：同时检查 node.id 和 node.backendId
              // 因为 node.id 保持为 pendingId，而流式更新可能传入后端 ID
              if (
                node.role === 'assistant' &&
                (String(node.id) === pendingId ||
                  String(node.backendId) === pendingId)
              ) {
                const newContents =
                  message.choices?.[0]?.blocks?.map((block: MessageBlock) => ({
                    type: block.type,
                    content: block.content,
                    createdAt: new Date().toISOString(),
                  })) || [];

                return {
                  ...node,
                  contents: newContents,
                  isStreaming: message.isStreaming,
                  tokens: message.tokens ?? node.tokens,
                  inputTokens: message.inputTokens ?? node.inputTokens,
                  messageStatus:
                    (message as any).messageStatus ?? node.messageStatus,
                };
              }

              if (node.children && node.children.length > 0) {
                return { ...node, children: updateNodeContents(node.children) };
              }
              return node;
            });
          };

          return { ...conv, treeData: updateNodeContents(conv.treeData) };
        })
      );
    },
    []
  );

  /**
   * 创建 user 和 assistant 节点对
   */
  const createUserAssistantNodePair = useCallback(
    (
      targetConversationId: string,
      message: Message,
      userInfo: {
        blocks: MessageBlock[];
        backendId?: number;
        fileIds?: string[];
      }
    ) => {
      setConversationsRef.current(prev =>
        prev.map(conv => {
          if (conv.id !== targetConversationId || !conv.treeData) return conv;

          // 创建 user 节点
          // 使用 message.id
          const userNodeId = userInfo.backendId || `user-${message.id}`;
          const userNode = {
            id: userNodeId,
            role: 'user' as const,
            contents:
              userInfo.blocks?.map((block: MessageBlock) => ({
                type: block.type,
                content: block.content,
                createdAt: new Date().toISOString(),
              })) || [],
            isStreaming: false,
            messageStatus: (message as any).messageStatus || 'pending', // 使用传入消息的状态
            previousMessageId: message.previousMessageId as any,
            backendId: userInfo.backendId,
            isCanceled: (message as any).isCanceled, // 🔥 新增：传递 isCanceled 属性
            children: [] as any[],
          };

          // 创建 assistant 节点
          // 使用 message.id
          const assistantNodeId = message.backendId || message.id;
          const assistantNode = {
            id: assistantNodeId,
            role: 'assistant' as const,
            contents: [],
            isStreaming: true,
            messageStatus: (message as any).messageStatus || 'pending', // 使用传入消息的状态
            previousMessageId: userInfo.backendId || userNodeId,
            backendId: message.backendId,
            client: message.client,
            isCanceled: (message as any).isCanceled, // 🔥 新增：传递 isCanceled 属性
            children: [] as any[],
          };

          // 将 assistant 作为 user 的子节点
          userNode.children = [assistantNode];

          // 查找父节点并添加
          let updatedTreeData = conv.treeData;

          const addChildToParent = (
            nodes: any[],
            parentId: any,
            child: any,
            depth: number = 0
          ): any[] | null => {
            for (let i = 0; i < nodes.length; i++) {
              const node = nodes[i];
              const idMatch = String(node.id) === String(parentId);
              const backendIdMatch =
                node.backendId && String(node.backendId) === String(parentId);

              if (idMatch || backendIdMatch) {
                // 🔥 重构：使用 String(c.id) === String(child.id) 匹配
                const existingChild = (node.children || []).find(
                  (c: any) =>
                    String(c.id) === String(child.id) ||
                    (child.backendId && c.backendId === child.backendId)
                );
                if (existingChild) {
                  return null;
                }

                return [
                  ...nodes.slice(0, i),
                  { ...node, children: [...(node.children || []), child] },
                  ...nodes.slice(i + 1),
                ];
              }
              if (node.children && node.children.length > 0) {
                const result = addChildToParent(
                  node.children,
                  parentId,
                  child,
                  depth + 1
                );
                if (result) {
                  return [
                    ...nodes.slice(0, i),
                    { ...node, children: result },
                    ...nodes.slice(i + 1),
                  ];
                }
              }
            }
            return null;
          };

          // 查找最深的叶子节点
          const findDeepestLeaf = (nodes: any[]): any | null => {
            if (!nodes || nodes.length === 0) return null;
            const lastNode = nodes[nodes.length - 1];
            if (lastNode.children && lastNode.children.length > 0) {
              return findDeepestLeaf(lastNode.children) || lastNode;
            }
            return lastNode;
          };

          if (message.previousMessageId) {
            const result = addChildToParent(
              conv.treeData,
              message.previousMessageId,
              userNode
            );
            if (result) {
              updatedTreeData = result;
            }
          }

          // 如果找不到父节点，添加到最深叶子或根级别
          if (updatedTreeData === conv.treeData) {
            const deepestNode = findDeepestLeaf(conv.treeData);
            if (deepestNode) {
              const result = addChildToParent(
                conv.treeData,
                deepestNode.id,
                userNode
              );
              if (result) {
                updatedTreeData = result;
              }
            } else {
              updatedTreeData = [userNode as any];
            }
          }

          return { ...conv, treeData: updatedTreeData };
        })
      );
    },
    []
  );

  /**
   * 完成消息处理（message_end）
   */
  const finalizeMessage = useCallback(
    (targetConversationId: string, message: Message) => {
      // 更新 treeData 节点状态
      setConversationsRef.current(prev =>
        prev.map(conv => {
          if (conv.id !== targetConversationId || !conv.treeData) return conv;

          const finalizeNode = (nodes: any[]): any[] => {
            return nodes.map(node => {
              // 🔥 修复：同时匹配 message.id、message.pendingId 和 node.backendId
              // node.id 可能是 pendingId（临时ID），而 message.id 可能是后端返回的数字ID
              // 所以需要多种匹配方式来确保能找到正确的节点
              const pendingId = (message as any).pendingId;
              const isMatch =
                node.role === 'assistant' &&
                (String(node.id) === String(message.id) ||
                  (pendingId && String(node.id) === String(pendingId)) ||
                  (node.backendId &&
                    String(node.backendId) === String(message.id)));
              if (isMatch) {
                const newContents =
                  message.choices?.[0]?.blocks?.length > 0
                    ? message.choices[0].blocks.map((block: MessageBlock) => ({
                        type: block.type,
                        content: block.content,
                        createdAt: new Date().toISOString(),
                      }))
                    : node.contents;

                return {
                  ...node,
                  contents: newContents,
                  isStreaming: false,
                  messageStatus: undefined,
                  tokens: message.tokens ?? node.tokens,
                  inputTokens: message.inputTokens ?? node.inputTokens,
                };
              }

              if (node.children && node.children.length > 0) {
                return { ...node, children: finalizeNode(node.children) };
              }
              return node;
            });
          };

          return { ...conv, treeData: finalizeNode(conv.treeData) };
        })
      );

      // 清除 user 节点的状态
      // 🔥 修复：user 节点 ID 格式是 `user-${pendingId}`，需要匹配这个格式
      const pendingId = (message as any).pendingId || message.id;
      setConversationsRef.current(prev =>
        prev.map(conv => {
          if (conv.id !== targetConversationId || !conv.treeData) return conv;

          const clearUserStatus = (nodes: any[]): any[] => {
            return nodes.map(node => {
              let updatedNode = { ...node };
              // 🔥 修复：匹配 user 节点 ID 格式 `user-${pendingId}` 或直接匹配
              if (
                node.role === 'user' &&
                (String(node.id) === String(pendingId) ||
                  String(node.id) === `user-${pendingId}` ||
                  String(node.id) === String(message.id) ||
                  String(node.id) === `user-${message.id}`)
              ) {
                updatedNode.messageStatus = undefined;
                updatedNode.isStreaming = false;
              }
              if (node.children && node.children.length > 0) {
                updatedNode.children = clearUserStatus(node.children);
              }
              return updatedNode;
            });
          };

          return { ...conv, treeData: clearUserStatus(conv.treeData) };
        })
      );
    },
    []
  );

  /**
   * 更新消息的后端 ID
   */
  const updateMessageBackendIds = useCallback(
    (targetConversationId: string, message: Message) => {
      setConversationsRef.current(prev =>
        prev.map(conv => {
          if (conv.id !== targetConversationId) return conv;

          const messages = conv.messages || [];
          let userMessageIndex = -1;
          let assistantMessageIndex = -1;

          // 🔥 重构：使用 String(msg.id) === String(message.id) 匹配
          messages.forEach((msg: Message, idx: number) => {
            if (
              String(msg.id) === String(message.id) &&
              msg.role === 'assistant'
            ) {
              assistantMessageIndex = idx;
              for (let i = idx - 1; i >= 0; i--) {
                if (messages[i].role === 'user' && !messages[i].backendId) {
                  userMessageIndex = i;
                  break;
                }
              }
            }
          });

          return {
            ...conv,
            messages: messages.map((msg: Message, idx: number) => {
              if (idx === assistantMessageIndex) {
                const userBackendId =
                  message.userMessageId ||
                  (userMessageIndex >= 0
                    ? messages[userMessageIndex].backendId
                    : undefined);
                return {
                  ...msg,
                  id: String(message.backendId),
                  backendId: message.backendId,
                  userMessageId: message.userMessageId,
                  previousMessageId: userBackendId || msg.previousMessageId,
                };
              }
              if (idx === userMessageIndex && message.userMessageId) {
                return {
                  ...msg,
                  id: String(message.userMessageId),
                  backendId: message.userMessageId,
                };
              }
              return msg;
            }),
          };
        })
      );
    },
    []
  );

  /**
   * 主消息处理函数
   */
  const handleIncomingMessage = useCallback(
    (message: Message, _targetConversationId?: string) => {
      const messageConversationId = (message as any).conversationId;

      // 第一步：确定目标 conversationId
      let targetConversationId: string | undefined =
        resolveTargetConversationId(message, conversationsRef.current);

      if (!targetConversationId) {
        console.warn(
          '❌ Cannot route message - no valid conversationId found',
          {
            messageConversationId,
            messageId: message.id, // 🔥 重构：使用 message.id
            messageRole: message.role,
          }
        );
        return;
      }

      // 第二步：查找目标 conversation
      let conversation = conversationsRef.current.find(
        conv => conv.id === targetConversationId
      );

      // 处理 temp- ID 到真实 ID 的转换
      // 🔥 重构：使用 isPendingId(message.id) 判断临时消息
      if (
        conversation &&
        targetConversationId &&
        !targetConversationId.startsWith('temp-') &&
        isPendingId(message.id)
      ) {
        const tempConv = findConversationByFrontendTempId(
          conversationsRef.current.filter(c => c.id.startsWith('temp-')),
          String(message.id)
        );

        if (
          tempConv &&
          (!conversation.messages || conversation.messages.length < 2)
        ) {
          const oldTempId = tempConv.id;
          const converted = convertTempToRealId(
            oldTempId,
            messageConversationId
          );
          if (converted) {
            conversation = converted;
            targetConversationId = messageConversationId;
          }
        }
      }

      // 如果找不到，尝试通过临时消息ID查找
      if (
        !conversation &&
        targetConversationId &&
        !targetConversationId.startsWith('temp-')
      ) {
        // 🔥 修复：先检查 message.pendingId（从 eventsource 传递），再检查 message.id
        const pendingIdToSearch =
          (message as any).pendingId ||
          (isPendingId(message.id) ? String(message.id) : null);
        if (pendingIdToSearch) {
          conversation = findConversationByFrontendTempId(
            conversationsRef.current,
            pendingIdToSearch
          );
          if (conversation) {
            // 🔥 修复：如果找到的 conversation 是 temp- ID，但 message 带有后端 conversationId，
            // 则立即转换 temp ID 为真实 ID
            if (
              conversation.id.startsWith('temp-') &&
              messageConversationId &&
              messageConversationId !== conversation.id
            ) {
              const converted = convertTempToRealId(
                conversation.id,
                messageConversationId
              );
              if (converted) {
                conversation = converted;
                targetConversationId = messageConversationId;
              } else {
                targetConversationId = conversation.id;
              }
            } else {
              targetConversationId = conversation.id;
            }
          }
        }

        if (!conversation) {
          const tempConvs = conversationsRef.current.filter(c =>
            c.id.startsWith('temp-')
          );
          if (tempConvs.length > 0) {
            conversation = tempConvs[0];
            targetConversationId = conversation.id;
          }
        }
      }

      if (!conversation) {
        console.warn('❌ Conversation not found:', targetConversationId);
        return;
      }

      // 更新消息
      if (targetConversationId) {
        updateConversationMessageRef.current(targetConversationId, message);
      }

      // 处理状态更新
      // 使用 String(message.id)
      if (isStatusOnlyUpdate(message) && conversation?.treeData?.length > 0) {
        updateTreeNodeStatus(
          targetConversationId!,
          String(message.id),
          message.role,
          (message as any).messageStatus
        );
        return;
      }

      // 处理 user + assistant 节点创建
      const userInfoData = getUserInfo(message);
      if (
        conversation?.treeData &&
        message.role === 'assistant' &&
        ((message as any).messageStatus === 'waiting' ||
          (message as any).messageStatus === 'pending') &&
        userInfoData &&
        !(message as any)._isBackendIdUpdate // 🔥 后端 ID 更新时不重新创建节点
      ) {
        // 🔥 新增：检查节点是否已存在（例如从队列中提前渲染的）
        const pendingId = (message as any).pendingId || String(message.id);
        const nodeExists = conversation.treeData.some(node => {
          const checkNode = (n: any): boolean => {
            if (
              String(n.id) === pendingId ||
              String(n.id) === `user-${pendingId}`
            ) {
              return true;
            }
            if (n.children && n.children.length > 0) {
              return n.children.some(checkNode);
            }
            return false;
          };
          return checkNode(node);
        });

        if (nodeExists) {
          // 节点已存在（从队列中提前渲染），只更新状态
          console.log(
            '📝 节点已存在，更新状态为',
            (message as any).messageStatus,
            { pendingId }
          );
          updateTreeNodeStatus(
            targetConversationId!,
            pendingId,
            'user',
            (message as any).messageStatus
          );
          updateTreeNodeStatus(
            targetConversationId!,
            pendingId,
            'assistant',
            (message as any).messageStatus
          );
        } else {
          // 节点不存在，创建新节点
          createUserAssistantNodePair(
            targetConversationId!,
            message,
            userInfoData
          );
        }
      }

      // 🔥 处理 ongoing 状态更新（message_start 时更新已渲染消息的状态）
      if (
        conversation?.treeData &&
        message.role === 'assistant' &&
        (message as any).messageStatus === 'ongoing' &&
        (message as any).pendingId
      ) {
        const pendingId = (message as any).pendingId;
        console.log('📝 更新消息状态为 ongoing', { pendingId });

        // 更新 user 和 assistant 节点的状态为 ongoing
        updateTreeNodeStatus(
          targetConversationId!,
          pendingId,
          'user',
          'ongoing'
        );
        updateTreeNodeStatus(
          targetConversationId!,
          pendingId,
          'assistant',
          'ongoing'
        );
      }

      // 🔥 处理后端 ID 更新（message_init 时更新已渲染消息的 ID）
      // 🔥 修复：移除 userInfoData 的强制要求，因为 assistant 节点的 backendId 更新不依赖它
      if (
        conversation?.treeData &&
        message.role === 'assistant' &&
        (message as any)._isBackendIdUpdate
      ) {
        // 只更新后端 ID，不重新创建节点
        const pendingId = (message as any).pendingId;
        const newAssistantId = message.id;
        const newUserBackendId = userInfoData?.backendId; // 🔥 修复：使用可选链，userInfoData 可能不存在

        console.log('📝 handleIncomingMessage: 更新后端 ID', {
          pendingId,
          newAssistantId,
          newAssistantIdType: typeof newAssistantId,
          newUserBackendId,
          hasTreeData: !!conversation?.treeData,
          treeDataLength: conversation?.treeData?.length,
        });

        // 更新 assistant 节点的 backendId
        setConversationsRef.current(prev =>
          prev.map(conv => {
            if (conv.id !== targetConversationId || !conv.treeData) return conv;

            const updateNodeIds = (nodes: any[]): any[] => {
              return nodes.map(node => {
                let updatedNode = { ...node };

                // 更新 user 节点的 backendId 和 messageStatus
                if (
                  node.role === 'user' &&
                  (String(node.id) === `user-${pendingId}` ||
                    String(node.id) === String(pendingId))
                ) {
                  if (newUserBackendId) {
                    updatedNode.backendId = newUserBackendId;
                  }
                  // 🔥 修复：同时更新 messageStatus 从 waiting 到 pending
                  if ((message as any).messageStatus) {
                    updatedNode.messageStatus = (message as any).messageStatus;
                  }
                }

                // 更新 assistant 节点的 backendId（保持 id 不变，避免破坏树结构）
                if (
                  node.role === 'assistant' &&
                  String(node.id) === String(pendingId)
                ) {
                  // 🔥 不要修改 node.id，只更新 backendId
                  // node.id 保持为 pendingId 用于前端匹配
                  // backendId 用于后端通信
                  updatedNode.backendId = newAssistantId;
                  // 🔥 修复：同时更新 previousMessageId 为 user 消息的后端 ID
                  // 这样删除消息时可以正确找到父消息（通过 backendId 匹配）
                  if (newUserBackendId) {
                    updatedNode.previousMessageId = newUserBackendId;
                  }
                  // 🔥 修复：同时更新 messageStatus 从 waiting 到 pending
                  if ((message as any).messageStatus) {
                    updatedNode.messageStatus = (message as any).messageStatus;
                  }
                }

                if (node.children && node.children.length > 0) {
                  updatedNode.children = updateNodeIds(node.children);
                }
                return updatedNode;
              });
            };

            return { ...conv, treeData: updateNodeIds(conv.treeData) };
          })
        );
      }

      // 处理 ongoing 状态更新
      if (
        conversation?.treeData &&
        message.role === 'assistant' &&
        (message as any).messageStatus === 'ongoing'
      ) {
        updateTreeNodeStatus(
          targetConversationId!,
          String(message.id),
          'assistant',
          'ongoing'
        );
        updateTreeNodeStatus(
          targetConversationId!,
          String(message.id),
          'user',
          'ongoing'
        );
      }

      // 处理流式内容更新
      if (
        message.role === 'assistant' &&
        message.isStreaming === true &&
        message.choices?.[0]?.blocks?.length > 0 &&
        !isStatusOnlyUpdate(message)
      ) {
        updateTreeNodeContents(
          targetConversationId!,
          String(message.id),
          message
        );
      }

      // 处理消息完成
      if (
        message.role === 'assistant' &&
        message.isStreaming === false &&
        !isStatusOnlyUpdate(message)
      ) {
        finalizeMessage(targetConversationId!, message);
      }

      // 更新后端 ID
      if (
        message.isStreaming === false &&
        message.backendId &&
        message.id !== String(message.backendId)
      ) {
        updateMessageBackendIds(targetConversationId!, message);
      }

      // 清除 loading 状态
      if (
        message?.isStreaming === false &&
        message.role === 'assistant' &&
        targetConversationId
      ) {
        // 🔥 修复：只有当没有其他活跃的流式消息 AND 没有等待中的任务时才清除 loading 状态
        // 这样侧边栏的 loading 会持续显示直到所有消息都完成
        const hasActiveStreaming = useUIStore
          .getState()
          .hasActiveStreamingMessages(targetConversationId);

        // 🔥 新增：检查任务队列中是否还有该对话的待处理任务
        // 因为任务队列一次只发送一条消息，activeStreamingMessages 可能为空
        // 但队列中还有其他任务等待发送
        const { taskQueue } = useUIStore.getState();
        const hasPendingTasks = taskQueue.some(
          task =>
            task.conversationId === targetConversationId &&
            ['pending', 'sent', 'initialized', 'streaming', 'waiting'].includes(
              task.status
            )
        );

        if (!hasActiveStreaming && !hasPendingTasks) {
          setConversationsRef.current(prev =>
            prev.map(conv =>
              conv.id === targetConversationId
                ? { ...conv, isLoading: false }
                : conv
            )
          );

          updateConversationLoadingStateRef.current?.(
            targetConversationId,
            false
          );

          // 🔥 对话完成后刷新侧边栏（Recent chats 和 Starred）
          window.refreshSidebarRecent?.();
          window.refreshSidebarStarred?.();
        }

        // 🔥 移除：不再在 message_end 后刷新对话详情
        // 原因：当多条消息快速发送时，服务器数据可能还没同步完成
        // 此时调用 loadConversationDetails 会用旧数据覆盖本地新添加的消息，导致消息丢失
        // 本地状态应该已经是正确的，不需要额外刷新
        // if (
        //   !hasOtherActiveTasks &&
        //   targetConversationId &&
        //   !targetConversationId.startsWith('temp-') &&
        //   !hasError
        // ) {
        //   setTimeout(() => {
        //     if (targetConversationId) {
        //       loadConversationDetailsRef.current?.(targetConversationId, true);
        //     }
        //   }, 500);
        // }
      }
    },
    [
      convertTempToRealId,
      updateTreeNodeStatus,
      updateTreeNodeContents,
      createUserAssistantNodePair,
      finalizeMessage,
      updateMessageBackendIds,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ]
  );

  return {
    handleIncomingMessage,
    convertTempToRealId,
    updateTreeNodeStatus,
    updateTreeNodeContents,
    createUserAssistantNodePair,
    finalizeMessage,
    updateMessageBackendIds,
  };
}

export default useMessageHandler;
